import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EnvironmentService } from '@evorait/pace-environment';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ONLINE_HEADER } from '@evorait/pace-offline';
import { EMPTY } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, take } from 'rxjs/operators';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { ConnectivityService } from '../../../services/connectivity/connectivity.service';
import { currentEndOfWork } from '../../timesheet/utils/end-of-work';
import { selectCurrentTimesheet } from '../../timesheet/selectors/timesheet.selector';
import { submitTimesheetRequested, updateStartOfWork } from '../../timesheet/actions/timesheet.actions';
import { setUserStoreValue } from '../../user-store/actions/user-store.actions';
import { companionPushRequested } from '../actions/location-tracker.actions';
import { companionPushRequestToPayload } from '../location-tracker.converters';
import { LOCATION_TRACKER_STATE_STORE_KEY } from '../location-tracker.state';
import {
    selectCompanionPushGate,
    selectCompanionStartOfWorkPushGate,
    selectStoredLocationTrackerTrackingState,
} from '../selectors/location-tracker.selector';

/**
 * Notifies the companion iOS app (via the PACE FX function's `/api/v1/ios-push/notify`) of
 * timesheet events the technician cares about. Kept as its own class rather than folded into
 * `LocationTrackerEffects` — an outbound push is a different concern from that class's
 * draft-seed/save/geolocation flow.
 *
 * Both events are gated on the stored `shareLocation` setting: it is the user's consent to being
 * tracked at all, so it governs every push, not just the one that starts the tracking. Neither
 * event pushes — nor records anything under `LOCATION_TRACKER_STATE_STORE_KEY` — while it is off.
 *
 * Both the `LocationTracker` (share/privacy settings) and `LocationTrackerState` (derived
 * work-day timestamps) keys now live in the user-store slice; this class no longer talks HTTP for
 * either read or write of `LocationTrackerState`, and there is no separate dedupe marker anymore —
 * `LocationTrackerState.startOfWork`, read back through `selectStoredLocationTrackerTrackingState`,
 * doubles as the dedupe (see `location-tracker.wire.ts`).
 */
@Injectable()
export class LocationTrackerCompanionPushEffects {
    private readonly url: string;

    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly http: HttpClient,
        private readonly environmentService: EnvironmentService,
        private readonly connectivityService: ConnectivityService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.url = this.environmentService.apiBase + ENDPOINTS.IosPushNotify;
    }

    /**
     * Decides whether a `startOfWork` push should actually go out.
     *
     * `updateStartOfWork` is not only the user starting work — `TimesheetEffects.pullBackStartOfWork$`
     * re-dispatches it after *every* `notificationLoaded` whenever a valid start of work exists,
     * even when the value did not change. Without a dedupe, that turns into a push on every
     * notification load and again after every app reload.
     *
     * `concatMap` (not `mergeMap`) must stay: the new stored value reaches the mirror through a
     * chain of synchronously dispatched actions (`companionPushRequested` -> `setUserStoreValue` ->
     * `userStoreEntryMirrored` -> reducer), so serializing successive `updateStartOfWork` values is
     * what keeps two of them from both passing the dedupe for the same value before either one's
     * `companionPushRequested` has had a chance to update the mirror.
     *
     * When `shareLocation` is `false`, nothing is pushed AND nothing is recorded — the stored
     * `startOfWork` is only ever written by `updateLocationTrackerState$`, which only runs on an
     * actual `companionPushRequested`. So enabling the switch later still pushes on the very next
     * `updateStartOfWork`, rather than being stuck deduped against a value it never actually
     * notified for.
     */
    public requestStartOfWorkCompanionPush$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateStartOfWork),
            filter(() => this.connectivityService.isInternetConnectionAvailable()),
            concatMap(({ startOfWork }) =>
                this.store.select(selectCompanionStartOfWorkPushGate).pipe(
                    filter((gate) => gate.isReady),
                    take(1),
                    filter((gate) => gate.shareLocation && gate.storedStartOfWork !== startOfWork),
                    map(() => companionPushRequested({ event: 'startOfWork', occurredAt: startOfWork, startOfWork })),
                ),
            ),
        ),
    );

    /**
     * `submitTimesheetRequested` is only ever dispatched by the facade's `submit()` on an explicit
     * user action, so unlike `updateStartOfWork` it needs no dedupe — it is gated on
     * `shareLocation` alone. A user who has not shared their location gets no companion push at
     * all, for either event; gating the start-of-work push only would have left the companion app
     * still being told when the work day ended.
     *
     * `mergeMap`, not the start-of-work effect's `concatMap`: with no dedupe there is no stored
     * value to serialize successive decisions against, so a submit whose gate is still waiting on
     * the user store must not hold a later one up behind it.
     *
     * `occurredAt` is taken when the submit arrives rather than after the gate answers, so a wait
     * for the user store to settle cannot backdate — or here, postdate — the event the companion
     * app is told about.
     *
     * Fires before the OData submit post (`TimesheetEffects.submitTimesheet$`), not after the
     * backend accepted it: if the submit then fails, the companion app has been told about a
     * timesheet that was not accepted. Switching to fire on acceptance instead is a one-line
     * change of the `ofType` below, should that trade-off change.
     */
    public requestTimesheetSubmittedCompanionPush$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitTimesheetRequested),
            filter(() => this.connectivityService.isInternetConnectionAvailable()),
            map(() => new Date().toISOString()),
            mergeMap((occurredAt) =>
                this.store.select(selectCompanionPushGate).pipe(
                    filter((gate) => gate.isReady),
                    take(1),
                    filter((gate) => gate.shareLocation),
                    map(() => companionPushRequested({ event: 'timesheetSubmitted', occurredAt })),
                ),
            ),
        ),
    );

    /**
     * Posts the push. `{ dispatch: false }`: the request is fire-and-forget from the store's point
     * of view, and a failure must never surface to the technician or interrupt the timesheet flow
     * that triggered it, so every failure is swallowed here rather than left for a caller to
     * handle — see the class doc.
     */
    public sendCompanionPush$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(companionPushRequested),
                concatMap((props) =>
                    this.http
                        .post<void>(this.url, companionPushRequestToPayload(props), {
                            headers: new HttpHeaders({ [ONLINE_HEADER]: ONLINE_HEADER }),
                        })
                        .pipe(
                            catchError((error) => {
                                this.log.error(
                                    `LocationTrackerCompanionPushEffects: sendCompanionPush$: '${props.event}' push failed: ${String(error)}`,
                                );
                                return EMPTY;
                            }),
                        ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Records the desired tracking state under `LOCATION_TRACKER_STATE_STORE_KEY`, triggered by the
     * SAME `companionPushRequested` as `sendCompanionPush$` — deliberately not independently gated,
     * because this entry represents the same start/stop-of-work activity as the push, not a
     * separate decision. Both requesting effects now gate on `shareLocation`, so a user who has not
     * shared their location leaves no work-day timestamps behind either. Unlike the old HTTP version, this dispatches into the user store rather
     * than posting directly, so it is not `{ dispatch: false }` — the dispatched `setUserStoreValue`
     * is itself fire-and-forget from this effect's point of view; `UserStoreEffects` owns surfacing
     * any write failure.
     *
     * A new work day invalidates the previous end of work: `event === 'startOfWork'` always resets
     * `endOfWork` to `undefined`, even if one was previously recorded.
     *
     * Both `startOfWork` and `endOfWork` may be in the future — the technician can enter a start or
     * end of work that has not happened yet, and the companion app uses them to schedule tracking
     * rather than to react immediately. That is the whole reason this key holds timestamps instead
     * of a boolean.
     */
    public updateLocationTrackerState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(companionPushRequested),
            concatLatestFrom(() => [
                this.store.select(selectStoredLocationTrackerTrackingState),
                this.store.select(selectCurrentTimesheet),
            ]),
            map(([props, trackingState, timesheet]) => {
                if (props.event === 'startOfWork') {
                    return setUserStoreValue({
                        key: LOCATION_TRACKER_STATE_STORE_KEY,
                        value: { startOfWork: props.startOfWork, endOfWork: undefined },
                    });
                }
                const endOfWork = timesheet ? currentEndOfWork(timesheet).toISOString() : new Date().toISOString();
                return setUserStoreValue({
                    key: LOCATION_TRACKER_STATE_STORE_KEY,
                    value: { startOfWork: trackingState.startOfWork, endOfWork },
                });
            }),
        ),
    );
}
